<template>
	<div>
		<v-container class="pa-3" fluid :style="fullscreen ? undefined : 'max-width: 1150px'">
			<!-- Fullscreen layout: assistance full height, remote/self stacked beside the exam camera -->
			<v-row v-if="fullscreen" dense class="fs-row">
				<div class="fs-exam-col">
					<!-- Examination Camera with PIP overlays -->
					<div class="exam-tile elevation-2">
						<span class="tile-label">{{ mainSlotLabel }}</span>
						<video ref="videoExam" autoplay playsinline muted class="video" />
						<div class="exam-controls">
							<button
								type="button"
								class="self-ctrl-btn"
								:disabled="!examSnapshotReady || snapshotInFlight"
								:title="snapshotInFlight ? $t('capturing-snapshot') : $t('snapshot-exam-tooltip')"
								:aria-label="$t('snapshot-examination-camera')"
								@click="snapshotExam">
								<v-icon size="20">{{ snapshotInFlight ? 'mdi-camera-iris' : 'mdi-camera' }}</v-icon>
							</button>
						</div>

						<!-- PIP: remote (patient) -->
						<div class="pip-tile elevation-3" :class="`pip-tile--${patientCorner}`" v-on="patientSwipe.listeners">
							<span class="tile-label">{{ pip1Label }}</span>
							<video ref="videoPatient" autoplay playsinline class="video" />
							<button type="button" class="pip-swap-btn" title="Swap with main" @click="swapWithExam('patient')">
								<v-icon size="16">mdi-arrow-expand</v-icon>
							</button>
						</div>

						<!-- PIP: self -->
						<div class="pip-tile elevation-3" :class="`pip-tile--${selfCorner}`" v-on="selfSwipe.listeners">
							<span class="tile-label">{{ pip2Label }}</span>
							<video ref="videoSelf" autoplay playsinline muted class="video mirror" />
							<div class="self-controls">
								<button
									type="button"
									class="self-ctrl-btn"
									:disabled="!localControlsEnabled"
									:title="localVideoEnabled ? $t('turn-camera-off') : $t('turn-camera-on')"
									:aria-label="localVideoEnabled ? $t('turn-camera-off') : $t('turn-camera-on')"
									@click="toggleLocalVideo">
									<v-icon size="18">{{ localVideoEnabled ? 'mdi-video' : 'mdi-video-off' }}</v-icon>
								</button>
								<button
									type="button"
									class="self-ctrl-btn"
									:disabled="!localControlsEnabled"
									:title="localAudioEnabled ? $t('mute-microphone') : $t('unmute-microphone')"
									:aria-label="localAudioEnabled ? $t('mute-microphone') : $t('unmute-microphone')"
									@click="toggleLocalAudio">
									<v-icon size="18">{{ localAudioEnabled ? 'mdi-microphone' : 'mdi-microphone-off' }}</v-icon>
								</button>
								<button
									type="button"
									class="self-ctrl-btn"
									:disabled="!localControlsEnabled"
									:title="$t('switch-camera')"
									:aria-label="$t('switch-camera')"
									@click="onSwitchLocalCamera">
									<v-icon size="18">mdi-camera-flip</v-icon>
								</button>
							</div>
							<button type="button" class="pip-swap-btn" title="Swap with main" @click="swapWithExam('self')">
								<v-icon size="16">mdi-arrow-expand</v-icon>
							</button>
						</div>
					</div>
				</div>

				<div class="fs-sidebar-col d-flex flex-column ml-2">
					<!-- Consultations List -->
					<v-card class="panel">
						<div class="panel-head d-flex align-center justify-space-between flex-wrap gap-2">
							<h2 class="subtitle-1 font-weight-bold mb-0">{{ $t('active-consults') }}</h2>
							<v-chip color="primary" small class="font-weight-bold">
								{{ displayedConsults.length }}
							</v-chip>
						</div>

						<ul v-if="displayedConsults.length > 0" class="stream-list">
							<li
								v-for="entry in displayedConsults"
								:key="entry.room"
								class="stream-item"
								:class="{ active: joinedRoomId === entry.room }"
								@click="joinRoom(entry.room)">
								<div class="stream-row d-flex align-center justify-space-between">
									<div class="stream-info d-inline-flex align-center flex-wrap">
										<v-icon :color="joinedRoomId === entry.room ? 'success' : 'primary'" size="18" class="mr-1">
											{{ joinedRoomId === entry.room ? 'mdi-check-circle' : 'mdi-video' }}
										</v-icon>
										<span v-if="entry.metadata && entry.metadata.patient && entry.metadata.patient.name" class="bit">
											<span class="text-medium-emphasis mr-1">{{ $t('patient') }}:</span>
											<strong>{{ entry.metadata.patient.name }}</strong>
										</span>
										<span v-if="operatorLabel(entry.metadata)" class="bit">
											<span class="text-medium-emphasis mr-1">{{ $t('operator') }}:</span>
											<strong>{{ operatorLabel(entry.metadata) }}</strong>
										</span>
										<span
											v-if="
												!(entry.metadata && entry.metadata.patient && entry.metadata.patient.name) &&
												!operatorLabel(entry.metadata)
											"
											class="bit">
											{{ patientLabel(entry.metadata) }}
										</span>
										<span class="stream-id">#{{ entry.room }}</span>
										<span class="caption text-medium-emphasis ml-2">
											{{ entry.numParticipants }} {{ $t('participants') }}
										</span>
									</div>

									<div class="d-flex align-center gap-2 flex-shrink-0 flex-wrap justify-end">
										<v-btn
											v-if="joinedRoomId === entry.room"
											color="error"
											depressed
											small
											:loading="endingConsultation"
											:disabled="endingConsultation"
											:aria-label="$t('end-consultation')"
											:title="$t('end-consultation-title')"
											class="end-consult-btn"
											@click.stop="onEndConsultation">
											<v-icon left small>mdi-phone-hangup</v-icon>
											{{ $t('end') }}
										</v-btn>
										<v-btn
											v-else
											icon
											x-small
											color="error"
											:loading="destroyingRoomIds.has(entry.room)"
											:disabled="destroyingRoomIds.has(entry.room)"
											:title="$t('destroy-consult-title')"
											:aria-label="$t('destroy-consult-room')"
											@click.stop="onDestroyRoom(entry.room)">
											<v-icon small>mdi-delete</v-icon>
										</v-btn>
									</div>
								</div>
							</li>
						</ul>

						<div v-else class="empty-placeholder text-medium-emphasis">
							{{ $t('no-active-consults') }}
						</div>
					</v-card>

					<!-- Assistance panel -->
					<v-card class="panel flex-grow-1 d-flex flex-column">
						<div class="d-flex align-center justify-space-between flex-wrap gap-1 mb-1">
							<div class="d-inline-flex align-center gap-2">
								<v-icon size="16" color="primary">mdi-message-text-outline</v-icon>
								<span class="caption font-weight-bold">{{ $t('assistance') }}</span>
								<v-chip x-small :color="assistanceStatusColor">
									{{ assistanceStatusLabel }}
								</v-chip>
							</div>
							<div class="d-flex gap-1">
								<v-btn
									x-small
									color="primary"
									depressed
									:disabled="assistanceConnected || assistanceConnecting"
									@click="onAssistanceConnect">
									<v-icon left x-small>mdi-chat-processing</v-icon>
									{{ $t('join') }}
								</v-btn>
								<v-btn
									x-small
									outlined
									:disabled="!assistanceConnected && !assistanceConnecting"
									@click="disconnectAssistance">
									<v-icon left x-small>mdi-chat-remove</v-icon>
									{{ $t('leave') }}
								</v-btn>
							</div>
						</div>

						<div ref="assistanceLogEl" class="assist-log" role="log">
							<p v-if="!assistanceLog.length" class="caption text-medium-emphasis ma-0 pa-1">
								<template v-if="assistanceConnecting">
									{{ $t('joining-assistance-room', { id: targetRoomId }) }}
								</template>
								<template v-else>{{ $t('assistance-room-join', { id: targetRoomId }) }}</template>
							</p>
							<template v-for="entry in assistanceLog">
								<div
									v-if="entry.kind === 'chat'"
									:key="`chat-${entry.id}`"
									class="assist-bubble-row"
									:class="{ 'assist-bubble-row--out': entry.outgoing }">
									<div class="assist-bubble" :class="entry.outgoing ? 'assist-bubble--out' : 'assist-bubble--in'">
										<div class="assist-bubble-meta d-flex align-center gap-1">
											<span class="assist-bubble-author">
												{{ entry.outgoing ? $t('you') : entry.author || $t('operator') }}
											</span>
											<span class="assist-bubble-sep">·</span>
											<span class="assist-bubble-time">
												{{ new Date(entry.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
											</span>
										</div>
										<span class="assist-bubble-text">{{ entry.line }}</span>
									</div>
								</div>
								<div v-else :key="`sys-${entry.id}`" class="assist-system text-center">{{ entry.line }}</div>
							</template>
						</div>

						<div class="py-2 d-flex align-center gap-2">
							<v-text-field
								v-model="assistanceDraft"
								dense
								outlined
								color="primary"
								hide-details
								:placeholder="$t('message-to-operator')"
								:disabled="!assistanceConnected"
								@keydown="assistanceOnKeydown" />
							<v-btn
								color="primary"
								:disabled="!assistanceConnected || !assistanceDraft.trim()"
								@click="assistanceOnSend">
								{{ $t('send') }}
							</v-btn>
						</div>
					</v-card>
				</div>
			</v-row>

			<!-- Windowed layout: consults across the top, remote/self below the exam camera -->
			<v-row v-else dense>
				<v-col cols="12">
					<!-- Consultations List -->
					<v-card class="panel">
						<div class="panel-head d-flex align-center justify-space-between flex-wrap gap-2">
							<h2 class="subtitle-1 font-weight-bold mb-0">{{ $t('active-consults') }}</h2>
							<v-chip color="primary" small class="font-weight-bold">
								{{ displayedConsults.length }}
							</v-chip>
						</div>

						<ul v-if="displayedConsults.length > 0" class="stream-list">
							<li
								v-for="entry in displayedConsults"
								:key="entry.room"
								class="stream-item"
								:class="{ active: joinedRoomId === entry.room }"
								@click="joinRoom(entry.room)">
								<div class="stream-row d-flex align-center justify-space-between">
									<div class="stream-info d-inline-flex align-center flex-wrap">
										<v-icon :color="joinedRoomId === entry.room ? 'success' : 'primary'" size="18" class="mr-1">
											{{ joinedRoomId === entry.room ? 'mdi-check-circle' : 'mdi-video' }}
										</v-icon>
										<span v-if="entry.metadata && entry.metadata.patient && entry.metadata.patient.name" class="bit">
											<span class="text-medium-emphasis mr-1">{{ $t('patient') }}:</span>
											<strong>{{ entry.metadata.patient.name }}</strong>
										</span>
										<span v-if="operatorLabel(entry.metadata)" class="bit">
											<span class="text-medium-emphasis mr-1">{{ $t('operator') }}:</span>
											<strong>{{ operatorLabel(entry.metadata) }}</strong>
										</span>
										<span
											v-if="
												!(entry.metadata && entry.metadata.patient && entry.metadata.patient.name) &&
												!operatorLabel(entry.metadata)
											"
											class="bit">
											{{ patientLabel(entry.metadata) }}
										</span>
										<span class="stream-id">#{{ entry.room }}</span>
										<span class="caption text-medium-emphasis ml-2">
											{{ entry.numParticipants }} {{ $t('participants') }}
										</span>
									</div>

									<div class="d-flex align-center gap-2 flex-shrink-0 flex-wrap justify-end">
										<v-btn
											v-if="joinedRoomId === entry.room"
											color="error"
											depressed
											small
											:loading="endingConsultation"
											:disabled="endingConsultation"
											:aria-label="$t('end-consultation')"
											:title="$t('end-consultation-title')"
											class="end-consult-btn"
											@click.stop="onEndConsultation">
											<v-icon left small>mdi-phone-hangup</v-icon>
											{{ $t('end') }}
										</v-btn>
										<v-btn
											v-else
											icon
											x-small
											color="error"
											:loading="destroyingRoomIds.has(entry.room)"
											:disabled="destroyingRoomIds.has(entry.room)"
											:title="$t('destroy-consult-title')"
											:aria-label="$t('destroy-consult-room')"
											@click.stop="onDestroyRoom(entry.room)">
											<v-icon small>mdi-delete</v-icon>
										</v-btn>
									</div>
								</div>
							</li>
						</ul>

						<div v-else class="empty-placeholder text-medium-emphasis">
							{{ $t('no-active-consults') }}
						</div>
					</v-card>
				</v-col>

				<v-col cols="8">
					<v-row dense>
						<!-- Examination Camera -->
						<v-col cols="12">
							<div class="exam-tile elevation-2">
								<span class="tile-label">{{ $t('examination-camera') }}</span>
								<video ref="videoExam" autoplay playsinline muted class="video" />
								<div class="exam-controls">
									<button
										type="button"
										class="self-ctrl-btn"
										:disabled="!examSnapshotReady || snapshotInFlight"
										:title="snapshotInFlight ? $t('capturing-snapshot') : $t('snapshot-exam-tooltip')"
										:aria-label="$t('snapshot-examination-camera')"
										@click="snapshotExam">
										<v-icon size="20">{{ snapshotInFlight ? 'mdi-camera-iris' : 'mdi-camera' }}</v-icon>
									</button>
								</div>
							</div>
						</v-col>

						<!-- Remote + Self cameras side by side -->
						<v-col cols="6">
							<div class="camera-tile elevation-2">
								<span class="tile-label">{{ patientTileLabel }}</span>
								<video ref="videoPatient" autoplay playsinline class="video" />
							</div>
						</v-col>
						<v-col cols="6">
							<div class="camera-tile elevation-2">
								<span class="tile-label">{{ $t('you') }}</span>
								<video ref="videoSelf" autoplay playsinline muted class="video mirror" />
								<div class="self-controls">
									<button
										type="button"
										class="self-ctrl-btn"
										:disabled="!localControlsEnabled"
										:title="localVideoEnabled ? $t('turn-camera-off') : $t('turn-camera-on')"
										:aria-label="localVideoEnabled ? $t('turn-camera-off') : $t('turn-camera-on')"
										@click="toggleLocalVideo">
										<v-icon size="18">{{ localVideoEnabled ? 'mdi-video' : 'mdi-video-off' }}</v-icon>
									</button>
									<button
										type="button"
										class="self-ctrl-btn"
										:disabled="!localControlsEnabled"
										:title="localAudioEnabled ? $t('mute-microphone') : $t('unmute-microphone')"
										:aria-label="localAudioEnabled ? $t('mute-microphone') : $t('unmute-microphone')"
										@click="toggleLocalAudio">
										<v-icon size="18">{{ localAudioEnabled ? 'mdi-microphone' : 'mdi-microphone-off' }}</v-icon>
									</button>
									<button
										type="button"
										class="self-ctrl-btn"
										:disabled="!localControlsEnabled"
										:title="$t('switch-camera')"
										:aria-label="$t('switch-camera')"
										@click="onSwitchLocalCamera">
										<v-icon size="18">mdi-camera-flip</v-icon>
									</button>
								</div>
							</div>
						</v-col>
					</v-row>
				</v-col>

				<v-col cols="4">
					<!-- Assistance panel -->
					<v-card class="panel h-100 d-flex flex-column">
						<div class="d-flex align-center justify-space-between flex-wrap gap-1 mb-1">
							<div class="d-inline-flex align-center gap-2">
								<v-icon size="16" color="primary">mdi-message-text-outline</v-icon>
								<span class="caption font-weight-bold">{{ $t('assistance') }}</span>
								<v-chip x-small :color="assistanceStatusColor">
									{{ assistanceStatusLabel }}
								</v-chip>
							</div>
							<div class="d-flex gap-1">
								<v-btn
									x-small
									color="primary"
									depressed
									:disabled="assistanceConnected || assistanceConnecting"
									@click="onAssistanceConnect">
									<v-icon left x-small>mdi-chat-processing</v-icon>
									{{ $t('join') }}
								</v-btn>
								<v-btn
									x-small
									outlined
									:disabled="!assistanceConnected && !assistanceConnecting"
									@click="disconnectAssistance">
									<v-icon left x-small>mdi-chat-remove</v-icon>
									{{ $t('leave') }}
								</v-btn>
							</div>
						</div>

						<div ref="assistanceLogEl" class="assist-log" role="log">
							<p v-if="!assistanceLog.length" class="caption text-medium-emphasis ma-0 pa-1">
								<template v-if="assistanceConnecting">
									{{ $t('joining-assistance-room', { id: targetRoomId }) }}
								</template>
								<template v-else>{{ $t('assistance-room-join', { id: targetRoomId }) }}</template>
							</p>
							<template v-for="entry in assistanceLog">
								<div
									v-if="entry.kind === 'chat'"
									:key="`chat-${entry.id}`"
									class="assist-bubble-row"
									:class="{ 'assist-bubble-row--out': entry.outgoing }">
									<div class="assist-bubble" :class="entry.outgoing ? 'assist-bubble--out' : 'assist-bubble--in'">
										<div class="assist-bubble-meta d-flex align-center gap-1">
											<span class="assist-bubble-author">
												{{ entry.outgoing ? $t('you') : entry.author || $t('operator') }}
											</span>
											<span class="assist-bubble-sep">·</span>
											<span class="assist-bubble-time">
												{{ new Date(entry.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
											</span>
										</div>
										<span class="assist-bubble-text">{{ entry.line }}</span>
									</div>
								</div>
								<div v-else :key="`sys-${entry.id}`" class="assist-system text-center">{{ entry.line }}</div>
							</template>
						</div>

						<div class="py-2 d-flex align-center gap-2">
							<v-text-field
								v-model="assistanceDraft"
								dense
								outlined
								color="primary"
								hide-details
								:placeholder="$t('message-to-operator')"
								:disabled="!assistanceConnected"
								@keydown="assistanceOnKeydown" />
							<v-btn
								color="primary"
								:disabled="!assistanceConnected || !assistanceDraft.trim()"
								@click="assistanceOnSend">
								{{ $t('send') }}
							</v-btn>
						</div>
					</v-card>
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script>
import { VideoConsultClient, CONSULT_DISPLAY_CLINICIAN } from '@/utils/videoroom-client.js';
import { ConsultDiscovery } from '@/utils/consult-discovery.js';
import { AssistanceTextRoomClient } from '@/utils/textroom-client.js';
import { buildAssistanceMessage, formatAssistanceLine } from '@/utils/assistance-messages.js';
import { mapState } from 'vuex';

const END_CONSULTATION_ACTION = 'endConsultation';
const SWIPE_THRESHOLD_PX = 32;
const SWIPE_IGNORE_SELECTOR = 'button, a, input, select, textarea, [role="button"]';

export default {
	name: 'ConsultView',
	props: {
		fullscreen: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		const params = new URLSearchParams(location.search);
		const routeRoom = this.$route && this.$route.query && this.$route.query.room;
		const routePatient = this.$route && this.$route.query && this.$route.query.patientId;
		return {
			serverUrl: (params.get('url') || '').trim() || 'wss://devdockerjanus.wellnessmetric.net',
			roomId: routeRoom ? Number(routeRoom) : null,
			displayName: (params.get('name') || '').trim() || 'Clinician',
			patientFilterId: routePatient ? Number(routePatient) : null,
			// Video consult
			vrConnected: false,
			publishing: false,
			localStream: null,
			examStream: null,
			patientStream: null,
			joinedRoomId: null,
			localVideoEnabled: true,
			localAudioEnabled: true,
			consultRooms: [],
			// Assistance
			assistanceConnected: false,
			assistanceConnecting: false,
			assistanceRoomId: null,
			assistanceLog: [],
			assistanceNextId: 1,
			assistanceDraft: '',
			// UI state
			destroyingRoomIds: new Set(),
			endingConsultation: false,
			patientAudioBlocked: false,
			patientCorner: 'top-right',
			selfCorner: 'bottom-right',
			swappedWithExam: null,
			snapshotInFlight: false,
		};
	},

	computed: {
		...mapState({
			authUser: (state) => state.authentication.userData,
			detailedUser: (state) => state.users.detailedUser,
		}),
		localControlsEnabled () {
			return !!this.localStream;
		},
		targetRoomId () {
			return this.joinedRoomId || this.roomId;
		},
		filteredConsultRooms () {
			const result = this.consultRooms;
			const hospitalId = this.authUser.hospitalAssignedData.id;
			const patientId = this.detailedUser.userData.id;
			return result.filter(
				(r) =>
					Number(r.metadata && r.metadata.hospital && r.metadata.hospital.id) === hospitalId &&
					Number(r.metadata && r.metadata.patient && r.metadata.patient.id) === patientId,
			);
		},
		displayedConsults () {
			return [...this.filteredConsultRooms].sort(
				(a, b) => ((b.metadata && b.metadata.createdAt) || 0) - ((a.metadata && a.metadata.createdAt) || 0),
			);
		},
		joinedConsultMeta () {
			if (!this.joinedRoomId) return null;
			const entry = this.consultRooms.find((r) => r.room === this.joinedRoomId);
			return (entry && entry.metadata) || null;
		},
		patientTileLabel () {
			const p = this.joinedConsultMeta && this.joinedConsultMeta.patient;
			return (
				(p && p.name && p.name.trim()) ||
				(p && p.id != null ? `Patient #${p.id}` : null) ||
				(this.patientFilterId ? `Patient #${this.patientFilterId}` : null) ||
				this.$t('remote')
			);
		},
		examSnapshotReady () {
			return !!this.examStream;
		},
		assistanceHasRoomId () {
			return Number(this.targetRoomId) > 0;
		},
		assistanceStatusColor () {
			if (this.assistanceConnected) return 'success';
			return this.assistanceHasRoomId ? 'warning' : undefined;
		},
		assistanceStatusLabel () {
			if (this.assistanceConnected) return this.$t('assistance-connected-room', { id: this.assistanceRoomId });
			if (this.assistanceConnecting && this.assistanceHasRoomId) {
				return this.$t('assistance-joining-room', { id: this.targetRoomId });
			}
			return this.assistanceHasRoomId
				? this.$t('assistance-pending-room', { id: this.targetRoomId })
				: this.$t('waiting-for-consult');
		},
		mainSlotLabel () {
			if (this.swappedWithExam === 'patient') return this.patientTileLabel;
			if (this.swappedWithExam === 'self') return this.$t('you');
			return this.$t('examination-camera');
		},
		pip1Label () {
			return this.swappedWithExam === 'patient' ? this.$t('examination-camera') : this.patientTileLabel;
		},
		pip2Label () {
			return this.swappedWithExam === 'self' ? this.$t('examination-camera') : this.$t('you');
		},
	},

	watch: {
		examStream () {
			this.$nextTick(() => this.rebindAllStreams());
		},
		patientStream (s) {
			if (!s) this.patientAudioBlocked = false;
			this.$nextTick(() => this.rebindAllStreams());
		},
		localStream () {
			this.$nextTick(() => this.rebindAllStreams());
		},
		swappedWithExam () {
			this.$nextTick(() => this.rebindAllStreams());
		},
		fullscreen (val) {
			if (!val) this.swappedWithExam = null;
			this.$nextTick(() => {
				this.rebindAllStreams();
				const log = this.$refs.assistanceLogEl;
				if (log) log.scrollTop = log.scrollHeight;
			});
		},
		serverUrl (url) {
			if (!url) return;
			this.startDiscovery(url);
		},
		assistanceLog () {
			this.$nextTick(() => {
				const el = this.$refs.assistanceLogEl;
				if (el) el.scrollTop = el.scrollHeight;
			});
		},
	},

	created () {
		this.videoClient = null;
		this.discovery = null;
		this.assistanceClient = null;
		this.pendingUnmuteListener = null;
		this.patientSwipe = this.createSwipeHandlers('patientCorner');
		this.selfSwipe = this.createSwipeHandlers('selfCorner');
	},

	mounted () {
		if (this.serverUrl) {
			this.startDiscovery(this.serverUrl);
		}
		const routeQuery = this.$route && this.$route.query;
		const roomQuery = routeQuery && routeQuery.room;
		const autojoin = routeQuery && routeQuery.autojoin === '1';
		if (roomQuery) {
			const id = Number(roomQuery);
			if (Number.isFinite(id) && id > 0) {
				this.roomId = id;
				if (autojoin) this.onJoin();
			}
		}
	},

	beforeDestroy () {
		this.stopDiscovery();
		this.onLeave();
		this.clearPendingUnmute();
		this.patientSwipe.cleanup();
		this.selfSwipe.cleanup();
	},

	methods: {
		// ---- Video consult -------------------------------------------------------

		connectVideo (serverUrl, roomId, displayName) {
			this.disconnectVideo();
			if (!roomId || roomId <= 0) {
				return;
			}
			this.joinedRoomId = roomId;
			this.localVideoEnabled = true;
			this.localAudioEnabled = true;
			this.videoClient = new VideoConsultClient(
				() => {},
				(pub, stream) => {
					this.publishing = pub;
					if (stream) {
						this.localStream = stream;
						this.localVideoEnabled = (stream.getVideoTracks()[0] && stream.getVideoTracks()[0].enabled) !== false;
						this.localAudioEnabled = (stream.getAudioTracks()[0] && stream.getAudioTracks()[0].enabled) !== false;
					} else {
						this.localStream = null;
					}
				},
			);
			this.videoClient.onRemoteStreams = () => {
				this.examStream = (this.videoClient && this.videoClient.getExamStream()) || null;
				this.patientStream = (this.videoClient && this.videoClient.getPatientStream()) || null;
				this.vrConnected = !!(this.examStream || this.patientStream || this.publishing);
			};
			this.videoClient.connect(serverUrl, roomId, displayName || CONSULT_DISPLAY_CLINICIAN);
			this.vrConnected = true;
		},

		disconnectVideo () {
			if (this.videoClient) this.videoClient.disconnect();
			this.videoClient = null;
			this.vrConnected = false;
			this.publishing = false;
			this.localStream = null;
			this.examStream = null;
			this.patientStream = null;
			this.joinedRoomId = null;
			this.localVideoEnabled = true;
			this.localAudioEnabled = true;
		},

		toggleLocalVideo () {
			if (!this.videoClient) return;
			this.localVideoEnabled = this.videoClient.setVideoEnabled(!this.localVideoEnabled);
		},

		toggleLocalAudio () {
			if (!this.videoClient) return;
			this.localAudioEnabled = this.videoClient.setAudioEnabled(!this.localAudioEnabled);
		},

		async onSwitchLocalCamera () {
			if (!this.videoClient) return;
			await this.videoClient.switchCamera();
		},

		// ---- Discovery -----------------------------------------------------------

		startDiscovery (serverUrl) {
			this.stopDiscovery();
			this.discovery = new ConsultDiscovery(
				(next) => {
					this.consultRooms = next;
				},
				() => {},
			);
			this.discovery.start(serverUrl, 2500);
		},

		stopDiscovery () {
			if (this.discovery) {
				this.discovery.stop();
				this.discovery = null;
			}
			this.consultRooms = [];
		},

		async destroyRoom (roomId) {
			if (!this.discovery) return { ok: false, roomId, error: 'Discovery not running' };
			return this.discovery.destroyRoom(roomId);
		},

		async onDestroyRoom (room) {
			const id = Number(room);
			if (!Number.isFinite(id) || id <= 0) return;
			if (!confirm(`Destroy consult room #${id}?\n\nThis disconnects everyone (operator + clinician).`)) return;
			this.destroyingRoomIds = new Set([...this.destroyingRoomIds, id]);
			try {
				if (this.joinedRoomId === id) this.onLeave();
				await this.destroyRoom(id);
			} finally {
				const next = new Set(this.destroyingRoomIds);
				next.delete(id);
				this.destroyingRoomIds = next;
			}
		},

		// ---- Room join/leave -----------------------------------------------------

		joinRoom (room) {
			const id = Number(room);
			if (!Number.isFinite(id) || id <= 0) {
				return;
			}
			this.roomId = id;
			this.connectVideo(this.serverUrl, id, this.displayName);
			this.connectAssistance(this.serverUrl, id, this.displayName, { role: 'clinician' });
		},

		onJoin () {
			this.joinRoom(this.roomId);
		},

		onLeave () {
			this.disconnectAssistance();
			this.disconnectVideo();
		},

		onEndConsultation () {
			if (this.endingConsultation) return;
			if (!this.joinedRoomId) {
				return;
			}
			this.executeEndConsultation();
		},

		async executeEndConsultation () {
			const room = this.joinedRoomId;
			if (!room) return;
			this.endingConsultation = true;
			try {
				let commandDelivered = false;
				if (this.assistanceConnected) {
					commandDelivered = this.sendAssistanceCommand(END_CONSULTATION_ACTION, { reason: 'clinician-ended' });
				}
				if (commandDelivered) {
					await new Promise((resolve) => setTimeout(resolve, 400));
				}
			} finally {
				this.onLeave();
				this.endingConsultation = false;
			}
			await this.destroyRoom(room);
		},

		// ---- Patient / operator labels -------------------------------------------

		patientLabel (meta) {
			const p = meta && meta.patient;
			if (!p) return 'Unknown patient';
			if (p.name && p.id != null) return `${p.name} (#${p.id})`;
			if (p.name) return p.name;
			if (p.id != null) return `Patient #${p.id}`;
			return 'Unknown patient';
		},

		operatorLabel (meta) {
			const o = meta && meta.operator;
			if (!o) return '';
			if (o.name && o.id != null) return `${o.name} (#${o.id})`;
			if (o.name) return o.name;
			if (o.id != null) return `Operator #${o.id}`;
			return '';
		},

		// ---- Video element binding -----------------------------------------------

		async bindStream (videoEl, stream, opts) {
			opts = opts || {};
			if (!videoEl) return;
			if (!stream) {
				videoEl.srcObject = null;
				if (opts.kind === 'patient') {
					this.patientAudioBlocked = false;
					this.clearPendingUnmute();
				}
				return;
			}
			videoEl.srcObject = stream;
			if (opts.kind === 'patient' && videoEl.muted) videoEl.muted = false;
			try {
				await videoEl.play();
				if (opts.kind === 'patient') {
					this.patientAudioBlocked = false;
					this.clearPendingUnmute();
				}
			} catch {
				if (opts.kind === 'patient' && !videoEl.muted) {
					this.patientAudioBlocked = true;
					videoEl.muted = true;
					try {
						await videoEl.play();
					} catch {
						/* still blocked */
					}
					this.armGlobalUnmuteFallback();
				}
			}
		},

		clearPendingUnmute () {
			if (this.pendingUnmuteListener) {
				document.removeEventListener('click', this.pendingUnmuteListener, true);
				document.removeEventListener('keydown', this.pendingUnmuteListener, true);
				document.removeEventListener('touchend', this.pendingUnmuteListener, true);
				this.pendingUnmuteListener = null;
			}
		},

		armGlobalUnmuteFallback () {
			this.clearPendingUnmute();
			this.pendingUnmuteListener = () => {
				const el = this.$refs.videoPatient;
				if (!el) {
					this.clearPendingUnmute();
					return;
				}
				el.muted = false;
				el.play()
					.then(() => {
						this.patientAudioBlocked = false;
					})
					.catch(() => {})
					.finally(() => this.clearPendingUnmute());
			};
			document.addEventListener('click', this.pendingUnmuteListener, true);
			document.addEventListener('keydown', this.pendingUnmuteListener, true);
			document.addEventListener('touchend', this.pendingUnmuteListener, true);
		},

		enablePatientAudio () {
			const el = this.$refs.videoPatient;
			if (!el) return;
			el.muted = false;
			el.play()
				.then(() => {
					this.patientAudioBlocked = false;
					this.clearPendingUnmute();
				})
				.catch(() => {});
		},

		swapWithExam (which) {
			this.swappedWithExam = this.swappedWithExam === which ? null : which;
		},

		rebindAllStreams () {
			const mainEl = this.$refs.videoExam;
			const pip1El = this.$refs.videoPatient;
			const pip2El = this.$refs.videoSelf;
			if (this.swappedWithExam === 'patient') {
				if (mainEl) mainEl.muted = false;
				if (pip1El) pip1El.muted = true;
				this.bindStream(mainEl, this.patientStream, { kind: 'patient' });
				this.bindStream(pip1El, this.examStream);
				this.bindStream(pip2El, this.localStream);
			} else if (this.swappedWithExam === 'self') {
				if (mainEl) mainEl.muted = true;
				if (pip2El) pip2El.muted = true;
				this.bindStream(mainEl, this.localStream);
				this.bindStream(pip1El, this.patientStream, { kind: 'patient' });
				this.bindStream(pip2El, this.examStream);
			} else {
				if (mainEl) mainEl.muted = true;
				this.bindStream(mainEl, this.examStream);
				this.bindStream(pip1El, this.patientStream, { kind: 'patient' });
				this.bindStream(pip2El, this.localStream);
			}
		},

		// ---- PIP swipe -----------------------------------------------------------

		createSwipeHandlers (cornerDataKey) {
			let startX = 0;
			let startY = 0;
			let pointerId = null;
			let docUpHandler = null;
			let docCancelHandler = null;

			const cleanup = () => {
				pointerId = null;
				if (docUpHandler) {
					document.removeEventListener('pointerup', docUpHandler);
					docUpHandler = null;
				}
				if (docCancelHandler) {
					document.removeEventListener('pointercancel', docCancelHandler);
					docCancelHandler = null;
				}
			};

			const applySwipe = (direction) => {
				const parts = this[cornerDataKey].split('-');
				const y = parts[0];
				const x = parts[1];
				let newCorner;
				switch (direction) {
					case 'left':
						newCorner = `${y}-left`;
						break;
					case 'right':
						newCorner = `${y}-right`;
						break;
					case 'up':
						newCorner = `top-${x}`;
						break;
					case 'down':
						newCorner = `bottom-${x}`;
						break;
				}
				if (!newCorner || newCorner === this[cornerDataKey]) return;
				const otherKey = cornerDataKey === 'patientCorner' ? 'selfCorner' : 'patientCorner';
				if (this[otherKey] === newCorner) {
					this[otherKey] = this[cornerDataKey];
				}
				this[cornerDataKey] = newCorner;
			};

			const onPointerdown = (ev) => {
				if (ev.button !== undefined && ev.button !== 0) return;
				if (ev.target.closest(SWIPE_IGNORE_SELECTOR)) return;
				cleanup();
				pointerId = ev.pointerId;
				startX = ev.clientX;
				startY = ev.clientY;
				docUpHandler = (e) => {
					if (e.pointerId !== pointerId) return;
					const dx = e.clientX - startX;
					const dy = e.clientY - startY;
					cleanup();
					const absDx = Math.abs(dx);
					const absDy = Math.abs(dy);
					if (absDx < SWIPE_THRESHOLD_PX && absDy < SWIPE_THRESHOLD_PX) return;
					applySwipe(absDx > absDy ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up');
				};
				docCancelHandler = (e) => {
					if (e.pointerId === pointerId) cleanup();
				};
				document.addEventListener('pointerup', docUpHandler);
				document.addEventListener('pointercancel', docCancelHandler);
			};

			return { cleanup, listeners: { pointerdown: onPointerdown } };
		},

		// ---- Snapshot ------------------------------------------------------------

		sanitizeFilenamePart (raw) {
			return String(raw == null ? '' : raw)
				.normalize('NFKD')
				.replace(/[^\w.-]+/g, '_')
				.replace(/_+/g, '_')
				.replace(/^_+|_+$/g, '');
		},

		formatDateTimeForFilename (d) {
			d = d || new Date();
			const pad = (n) => String(n).padStart(2, '0');
			return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(
				d.getMinutes(),
			)}-${pad(d.getSeconds())}`;
		},

		patientFilenamePart () {
			const meta = this.joinedConsultMeta;
			const p = meta && meta.patient;
			const nameRaw = p && p.name && p.name.trim();
			if (nameRaw) {
				const s = this.sanitizeFilenamePart(nameRaw);
				if (s) return s;
			}
			if (p && p.id != null) return `Patient_${p.id}`;
			if (this.patientFilterId) return `Patient_${this.patientFilterId}`;
			return 'Unknown';
		},

		async captureBestExamFrame () {
			const stream = this.examStream;
			if (!stream) return { error: 'Snapshot: no examination camera stream connected.' };
			const track = stream.getVideoTracks()[0];
			if (!track) return { error: 'Snapshot: examination camera has no video track.' };
			if (typeof window.ImageCapture === 'function') {
				try {
					const capture = new window.ImageCapture(track);
					const bitmap = await capture.grabFrame();
					return {
						source: bitmap,
						width: bitmap.width,
						height: bitmap.height,
						close: () => {
							if (typeof bitmap.close === 'function') bitmap.close();
						},
					};
				} catch {
					// fall through to <video> element fallback
				}
			}
			const videoEl = this.$refs.videoExam;
			if (!videoEl) return { error: 'Snapshot: exam video element not mounted yet.' };
			const ts = typeof track.getSettings === 'function' ? track.getSettings() : {};
			const w = Math.max(videoEl.videoWidth | 0, ts.width | 0 || 0);
			const h = Math.max(videoEl.videoHeight | 0, ts.height | 0 || 0);
			if (w <= 0 || h <= 0) {
				return {
					error: 'Snapshot: no examination camera frame yet — plug in the UVC camera and wait for the first frame.',
				};
			}
			return { source: videoEl, width: w, height: h };
		},

		async snapshotExam () {
			if (this.snapshotInFlight) return;
			this.snapshotInFlight = true;
			try {
				const capture = await this.captureBestExamFrame();
				if (capture.error) {
					return;
				}
				const { source, width, height, close } = capture;
				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					return;
				}
				ctx.imageSmoothingEnabled = true;
				ctx.imageSmoothingQuality = 'high';
				try {
					ctx.drawImage(source, 0, 0, width, height);
				} catch {
					return;
				} finally {
					if (close) close();
				}
				const filename = `Examination_camera_${this.patientFilenamePart()}_${this.formatDateTimeForFilename()}.jpg`;
				const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 1.0));
				if (!blob) {
					return;
				}
				const url = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				setTimeout(() => {
					document.body.removeChild(a);
					URL.revokeObjectURL(url);
				}, 0);
			} finally {
				this.snapshotInFlight = false;
			}
		},

		// ---- Assistance ----------------------------------------------------------

		appendAssistanceLog (line, kind, outgoing, author) {
			kind = kind || 'system';
			outgoing = outgoing || false;
			this.assistanceLog = [
				...this.assistanceLog,
				{ id: this.assistanceNextId++, at: Date.now(), line, kind, outgoing, author },
			].slice(-200);
		},

		connectAssistance (serverUrl, roomId, displayName, opts) {
			opts = opts || {};
			this.disconnectAssistance();
			if (!roomId || roomId <= 0) {
				this.appendAssistanceLog('Set a valid room id first.');
				return;
			}
			this.assistanceConnecting = true;
			this.assistanceClient = new AssistanceTextRoomClient(
				(isUp) => {
					this.assistanceConnecting = false;
					this.assistanceConnected = isUp;
					this.assistanceRoomId = isUp ? roomId : null;
				},
				(msg) => {
					if (msg.type === 'chat') {
						const text = String((msg.payload && msg.payload.text) || '').trim();
						if (!text) return;
						this.appendAssistanceLog(text, 'chat', false, (msg.from && msg.from.name) || (msg.from && msg.from.role));
						return;
					}
					if (msg.type === 'device' && msg.payload && msg.payload.event === 'heartRate') return;
					this.appendAssistanceLog(formatAssistanceLine(msg), msg.type, false);
				},
				() => {},
				() => {},
			);
			this.appendAssistanceLog(`Connecting assistance room #${roomId}…`);
			this.assistanceClient.connect(serverUrl, roomId, displayName, opts);
		},

		disconnectAssistance (opts) {
			if (this.assistanceClient) this.assistanceClient.disconnect();
			this.assistanceClient = null;
			this.assistanceConnecting = false;
			this.assistanceConnected = false;
			this.assistanceRoomId = null;
			if (opts && opts.clearLog) this.assistanceLog = [];
		},

		sendAssistanceChat (text) {
			const trimmed = text.trim();
			if (!trimmed) return;
			if (this.assistanceClient) this.assistanceClient.sendChat(trimmed);
			this.appendAssistanceLog(trimmed, 'chat', true);
		},

		sendAssistanceCommand (action, extras) {
			extras = extras || {};
			if (!this.assistanceClient) return false;
			this.assistanceClient.sendAssistance(
				buildAssistanceMessage({
					type: 'command',
					appointmentId: this.joinedRoomId,
					from: { role: 'clinician', name: this.displayName || undefined },
					payload: Object.assign({ action }, extras),
				}),
			);
			return true;
		},

		onAssistanceConnect () {
			const room = this.targetRoomId;
			if (!room) {
				this.appendAssistanceLog('Open a video consult first — the room id comes from its metadata.');
				return;
			}
			this.connectAssistance(this.serverUrl, room, this.displayName, { role: 'clinician' });
		},

		assistanceOnSend () {
			const text = this.assistanceDraft.trim();
			if (!text) return;
			this.sendAssistanceChat(text);
			this.assistanceDraft = '';
		},

		assistanceOnKeydown (e) {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				this.assistanceOnSend();
			}
		},
	},
};
</script>

<style scoped>
/* Layout utilities */
.gap-1 {
	gap: 4px;
}
.gap-2 {
	gap: 8px;
}
.h-100 {
	height: 100%;
}
.flex-shrink-0 {
	flex-shrink: 0;
}
.text-medium-emphasis {
	color: rgba(0, 0, 0, 0.6);
}

/* ---- Consult list ---- */
.panel {
	padding: 10px 12px;
}

.panel-head {
	margin-bottom: 6px;
}

.stream-list {
	list-style: none;
	margin: 0;
	padding: 0;
}

.stream-item {
	display: block;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 8px;
	margin-bottom: 6px;
	cursor: pointer;
	transition: background 0.15s, border-color 0.15s;
	list-style: none;
	background: #fff;
}

.stream-row {
	gap: 10px;
	padding: 8px 10px;
}

.stream-info {
	gap: 4px 14px;
	font-size: 13px;
	min-width: 0;
	flex: 1;
}

.stream-id {
	font-weight: 600;
	color: #6b6f76;
	opacity: 0.65;
	font-variant-numeric: tabular-nums;
}

.stream-item:hover {
	background: #fafbfc;
	border-color: #c8ccd4;
}

.stream-item.active {
	border-color: #a51717;
	background: #fff5f5;
}

.empty-placeholder {
	padding: 12px 4px;
	font-size: 13px;
}

/* ---- Video stage ---- */
.exam-tile {
	position: relative;
	width: 100%;
	aspect-ratio: 16 / 9;
	background: #111;
	border-radius: 4px;
	overflow: hidden;
	min-height: 200px;
}

.exam-tile > .video {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.camera-tile {
	position: relative;
	flex: 1;
	aspect-ratio: 4 / 3;
	background: #000;
	border-radius: 4px;
	overflow: hidden;
}

.camera-tile > .video {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

/* PIP tiles (fullscreen mode) */
.pip-tile {
	position: absolute;
	width: 22%;
	aspect-ratio: 4 / 3;
	background: #000;
	border-radius: 6px;
	overflow: hidden;
	cursor: grab;
	touch-action: none;
	z-index: 4;
	border: 2px solid rgba(255, 255, 255, 0.3);
	transition: top 0.18s ease, right 0.18s ease, bottom 0.18s ease, left 0.18s ease;
}

.pip-tile:active {
	cursor: grabbing;
}

.pip-tile--top-right {
	top: 10px;
	right: 10px;
	bottom: auto;
	left: auto;
}

.pip-tile--top-left {
	top: 10px;
	left: 10px;
	bottom: auto;
	right: auto;
}

.pip-tile--bottom-right {
	bottom: 10px;
	right: 10px;
	top: auto;
	left: auto;
}

.pip-tile--bottom-left {
	bottom: 10px;
	left: 10px;
	top: auto;
	right: auto;
}

.pip-tile > .video {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.pip-tile .tile-label {
	z-index: 5;
}

.pip-tile .self-controls {
	bottom: 4px;
	z-index: 5;
}

.pip-swap-btn {
	position: absolute;
	bottom: 5px;
	right: 5px;
	z-index: 5;
	width: 22px;
	height: 22px;
	border-radius: 50%;
	border: none;
	background: rgba(0, 0, 0, 0.55);
	color: #fff;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;
	transition: background-color 120ms ease;
}

.pip-swap-btn:hover {
	background: rgba(0, 0, 0, 0.8);
}

.pip-swap-btn ::v-deep .v-icon {
	color: #fff !important;
}

/* ---- Fullscreen height-driven layout ---- */
/*
 * The exam tile height is driven by the viewport: 100vh minus the dialog
 * header (~49px: card-title 48px + divider 1px) minus the container's
 * top+bottom padding (pa-3 = 12px each side = 24px).
 * The 16:9 aspect-ratio then determines the width automatically.
 * Adjust the 73px offset here if the dialog header height changes.
 */
.fs-row {
	flex-wrap: nowrap !important;
	align-items: flex-start;
	margin: 0 !important;
}

.fs-exam-col {
	flex: 0 0 auto;
}

.fs-exam-col > .exam-tile {
	height: calc(100vh - 80.8px);
	width: auto;
	min-height: unset;
}

.fs-sidebar-col {
	flex: 1 1 0;
	min-width: 240px;
	height: calc(100vh - 80.8px);
	gap: 8px;
	overflow: hidden;
}

.tile-label {
	position: absolute;
	top: 6px;
	left: 8px;
	z-index: 3;
	color: #fff;
	font-size: 11px;
	background: rgba(0, 0, 0, 0.5);
	padding: 2px 6px;
	border-radius: 4px;
}

.video {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.mirror {
	transform: scaleX(-1);
}

.self-controls {
	position: absolute;
	bottom: 6px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	gap: 6px;
	z-index: 3;
}

.exam-controls {
	position: absolute;
	bottom: 12px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	gap: 8px;
	z-index: 3;
}

.exam-controls .self-ctrl-btn {
	width: 40px;
	height: 40px;
}

.self-ctrl-btn {
	width: 30px;
	height: 30px;
	border-radius: 50%;
	border: none;
	background: rgba(0, 0, 0, 0.55);
	color: #fff;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;
	transition: background-color 120ms ease, transform 120ms ease;
}

.self-ctrl-btn ::v-deep .v-icon {
	color: #fff !important;
}

.self-ctrl-btn:hover:not(:disabled) {
	background: rgba(0, 0, 0, 0.75);
}

.self-ctrl-btn:active:not(:disabled) {
	transform: scale(0.94);
}

.self-ctrl-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

@media (max-width: 600px) {
	.self-ctrl-btn {
		width: 26px;
		height: 26px;
	}
}

/* ---- Assistance panel ---- */
.assist-log {
	flex: 1 1 0;
	min-height: 0;
	overflow-y: auto;
	padding: 6px 12px;
	font-size: 12px;
	line-height: 1.45;
	background: #fafafa;
}

.assist-bubble-row {
	display: flex;
	margin: 2px 0;
}

.assist-bubble-row--out {
	justify-content: flex-end;
}

.assist-bubble {
	max-width: 75%;
	padding: 5px 9px 4px;
	border-radius: 12px;
	font-size: 12px;
	line-height: 1.35;
	display: inline-flex;
	flex-direction: column;
	word-break: break-word;
}

.assist-bubble--in {
	background: #f1f3f5;
	border: 1px solid #e1e4e8;
	color: #1f2328;
	border-bottom-left-radius: 3px;
}

.assist-bubble--out {
	background: #a51717;
	color: #fff;
	border-bottom-right-radius: 3px;
}

.assist-bubble-meta {
	font-size: 10px;
	opacity: 0.7;
	margin-bottom: 2px;
}

.assist-bubble-author {
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.assist-bubble-sep {
	opacity: 0.6;
}

.assist-bubble-time {
	font-variant-numeric: tabular-nums;
}

.assist-bubble-text {
	white-space: pre-wrap;
}

.assist-system {
	color: #6b6f76;
	font-size: 11px;
	font-style: italic;
	margin: 3px 0;
}
</style>
